import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtNum } from "@/lib/api";

const METRIC_ROWS = [
  { key: "qualified_signals",       label: "Qualified signals",       fmt: (v) => v },
  { key: "accepted_trades",         label: "Accepted trades",         fmt: (v) => v },
  { key: "risk_rejections",         label: "Risk rejections",         fmt: (v) => v },
  { key: "total_trades",            label: "Closed trades",           fmt: (v) => v },
  { key: "wins",                    label: "Wins",                    fmt: (v) => v },
  { key: "losses",                  label: "Losses",                  fmt: (v) => v },
  { key: "win_rate_pct",            label: "Win rate",                fmt: (v) => `${fmtNum(v, 2)}%` },
  { key: "gross_pnl",               label: "Gross P&L",               fmt: fmtMoney },
  { key: "total_charges",           label: "Total charges",           fmt: fmtMoney },
  { key: "total_slippage",          label: "Slippage",                fmt: fmtMoney },
  { key: "net_pnl",                 label: "Net P&L",                 fmt: fmtMoney, hi: true },
  { key: "avg_win",                 label: "Average win",             fmt: fmtMoney },
  { key: "avg_loss",                label: "Average loss",            fmt: fmtMoney },
  { key: "profit_factor",           label: "Profit factor",           fmt: (v) => (v == null ? "∞" : fmtNum(v, 3)) },
  { key: "expectancy_per_trade",    label: "Expectancy / trade",      fmt: fmtMoney },
  { key: "max_drawdown",            label: "Max drawdown",            fmt: fmtMoney },
  { key: "consecutive_losses",      label: "Max consecutive losses",  fmt: (v) => v },
  { key: "return_on_capital_pct",   label: "Return on capital",       fmt: (v) => `${fmtNum(v, 3)}%` },
];

const WINDOWS = [
  { key: "7",    label: "7d" },
  { key: "30",   label: "30d" },
  { key: "90",   label: "90d" },
  { key: "all",  label: "All-time" },
];

export default function Comparison() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [window, setWindow] = useState("all");
  const [tab, setTab] = useState("combined"); // 'nifty' | 'sensex' | 'combined'

  const load = async () => {
    setBusy(true);
    try {
      const params = window === "all" ? {} : { days: Number(window) };
      const { data } = await api.get("/strategy/comparison", { params });
      setData(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("comparison load failed:", e?.message);
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const currentMetrics = data
    ? (tab === "combined" ? data.combined : (data.by_underlying?.[tab.toUpperCase()] || {}))
    : {};

  const sensex = data?.sensex_readiness;

  return (
    <div className="space-y-4" data-testid="comparison-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Strategy Comparison</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
            Paper-trading performance grouped by underlying
          </p>
        </div>
        <div className="flex gap-1" data-testid="comparison-window">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              data-testid={`window-${w.key}`}
              onClick={() => setWindow(w.key)}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] rounded-sm border ${
                window === w.key
                  ? "border-blue-500 bg-blue-500/20 text-blue-300"
                  : "border-slate-700 text-slate-400 hover:bg-slate-800/50"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* SENSEX ready-for-live gauge */}
      {sensex && (
        <div className={`surface rounded-sm p-4 border-l-2 ${sensex.ready_for_live ? "border-l-emerald-500" : "border-l-amber-500"}`} data-testid="sensex-readiness-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                SENSEX live-trading gate
              </div>
              <div className="text-lg font-mono">
                <span data-testid="sensex-sessions-count">{sensex.paper_sessions_completed}</span>
                <span className="text-slate-500"> / {sensex.paper_sessions_required} paper sessions</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                Expectancy/trade: <span className="font-mono">{fmtMoney(sensex.expectancy_per_trade)}</span>
                {" · "}
                Profit factor: <span className="font-mono">{sensex.profit_factor == null ? "∞" : fmtNum(sensex.profit_factor, 3)}</span>
              </div>
            </div>
            <div
              className={`text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm ${
                sensex.ready_for_live
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500"
                  : "bg-amber-500/20 text-amber-300 border border-amber-500"
              }`}
              data-testid="sensex-ready-flag"
            >
              {sensex.ready_for_live ? "Ready for live" : "Not ready"}
            </div>
          </div>
          <div className="text-[10px] text-slate-500 mt-3 leading-relaxed">
            Live-trading of SENSEX unlocks after 30 paper sessions with positive expectancy AND profit factor &gt; 1.0. Universe flip to `enabled` still requires explicit approval on the Algorithm page.
          </div>
        </div>
      )}

      {/* Underlying tabs */}
      <div className="flex gap-1" data-testid="comparison-tabs">
        {[
          { key: "combined", label: "Combined" },
          { key: "nifty",    label: "NIFTY" },
          { key: "sensex",   label: "SENSEX" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] rounded-sm border ${
              tab === t.key
                ? "border-blue-500 bg-blue-500/20 text-blue-300"
                : "border-slate-700 text-slate-400 hover:bg-slate-800/50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="surface rounded-sm p-4" data-testid="comparison-metrics">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
          {tab === "combined" ? "Combined" : tab.toUpperCase()} — {WINDOWS.find((w) => w.key === window)?.label}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 font-mono text-xs">
          {METRIC_ROWS.map((r) => (
            <div
              key={r.key}
              data-testid={`metric-${r.key}`}
              className={`flex justify-between border-b border-slate-800/60 py-1 ${
                r.hi ? "text-slate-100" : "text-slate-300"
              }`}
            >
              <span className="text-slate-500">{r.label}</span>
              <span className={r.hi ? "font-medium" : ""}>
                {r.fmt(currentMetrics?.[r.key])}
              </span>
            </div>
          ))}
        </div>
        {busy && (
          <div className="mt-3 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Loading…
          </div>
        )}
      </div>

      <div className="surface rounded-sm p-4 border-l-2 border-l-amber-500">
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-2">Disclosure</div>
        <p className="text-xs text-slate-300 leading-relaxed">
          Paper-trading results include modeled charges + estimated slippage but do NOT include order-queue latency, real-market spread expansion or Zerodha rejection reprocessing. Positive paper expectancy does not guarantee live results.
        </p>
      </div>
    </div>
  );
}
